import React from 'react'
import { NavLink, Link as RouterLink } from 'react-router-dom'
import request from '../../api/request'
import apiBaseUrl from '../../api/url'
import Section from '../shared/section'
import Loading from '../shared/loading'
import ErrorUI from '../shared/error_ui'
import Notes from './notes'
import CopyPath from '../shared/copy_path'
import RateLimitsTable from '../shared/rate_limit'
import {
  StackView,
  Badge,
  Box,
  Alert,
  Icon,
} from '@planningcenter/tapestry-react'
import { Link, List } from '../shared/typography'
import Table from '../shared/table'
import { DeprecatedBadge } from '../shared/badges'
import {
  PageHeading,
  Heading,
  Text,
  Markdown,
  Code,
  CodeBlock,
} from '../shared/typography'

function trimUrl(string) {
  return string.split('.com').pop()
}

function pathWithEnv(path) {
  return `${apiBaseUrl}${trimUrl(path)}`
}

function explorerUrl(path) {
  return `${apiBaseUrl}/explorer${trimUrl(path)}`
}

function objectHasData(obj) {
  for (var key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) return true
  }
  return false
}

const Pagination = ({ perPage, offset }) => {
  const columns = [
    {
      header: 'Name',
      cell: 'name',
    },
    {
      header: 'Parameter',
      cell: 'parameter',
    },
    {
      header: 'Type',
      cell: 'type',
    },
    {
      header: 'Description',
      cell: 'description',
    },
  ]

  const data = [perPage, offset].map(obj => ({
    name: obj.attributes.name,
    parameter: obj.attributes.parameter,
    type: obj.attributes.type,
    description: obj.attributes.description,
  }))

  return (
    <Section name="Pagination" isSub={true}>
      <Box marginTop={1}>
        <Table columns={columns} data={data} />
      </Box>
    </Section>
  )
}

const Includeable = ({ includable }) => {
  const columns = [
    {
      header: 'Parameter',
      cell: 'parameter',
    },
    {
      header: 'Value',
      cell: 'value',
    },
    {
      header: 'Description',
      cell: 'description',
    },
  ]

  // TODO - temporary: this if can go away and we can always include the assign
  // column in the columns literal sometime after every app is using a
  // version of pco-api that includes this field (version 12, i think)
  if (
    includable.some(obj =>
      Object.prototype.hasOwnProperty.call(
        obj.attributes,
        'can_assign_on_create'
      )
    )
  ) {
    columns.push({ header: 'Assignable', cell: 'assignable' })
  }

  const data = includable.map(obj => ({
    parameter: obj.attributes.parameter,
    value: obj.attributes.value,
    description: obj.attributes.description,
    assignable: [
      obj.attributes.can_assign_on_create && 'create',
      obj.attributes.can_assign_on_update && 'update',
    ]
      .filter(Boolean)
      .join(' and '),
  }))

  return (
    <Section collection={includable} name="Can Include" isSub={true}>
      <Box marginTop={1}>
        <Table columns={columns} data={data} />
      </Box>
    </Section>
  )
}

const Queryable = ({ queryable }) => {
  const columns = [
    {
      header: 'Name',
      cell: 'name',
    },
    {
      header: 'Parameter',
      cell: 'parameter',
    },
    {
      header: 'Type',
      cell: 'type',
    },
    {
      header: 'Description',
      cell: row => (
        <Box whiteSpace="normal" shrink={1}>
          <Markdown>{row.description || ''}</Markdown>
        </Box>
      ),
      css: { maxWidth: 600 },
    },
    {
      header: 'Example',
      cell: 'example',
    },
  ]

  const helpByAttributeType = type => {
    return {
      date: '/overview/dates-times',
      date_time: '/overview/dates-times',
    }[type]
  }

  const exampleWithHelp = obj => {
    const helpPage = helpByAttributeType(obj.attributes.type)

    return (
      <span>
        <Code>{obj.attributes.example}</Code>
        {helpPage ? (
          <NavLink to={helpPage}>
            <Icon name="general.questionCircle" inline />
          </NavLink>
        ) : null}
      </span>
    )
  }

  const data = queryable.map(obj => ({
    name: obj.attributes.name,
    parameter: obj.attributes.parameter,
    type: obj.attributes.type,
    description: obj.attributes.description,
    example: exampleWithHelp(obj),
  }))

  return (
    <Section name="Query By" collection={queryable} isSub={true}>
      <Box marginTop={1}>
        <Table columns={columns} data={data} />
      </Box>
    </Section>
  )
}

const Orderable = ({ orderable }) => {
  const columns = [
    {
      header: 'Parameter',
      cell: 'parameter',
    },
    {
      header: 'Value',
      cell: 'value',
    },
    {
      header: 'Type',
      cell: 'type',
    },
    {
      header: 'Description',
      cell: 'description',
    },
  ]

  const data = orderable.map(obj => ({
    parameter: obj.attributes.parameter,
    value: obj.attributes.value,
    type: obj.attributes.type,
    description: obj.attributes.description,
  }))

  return (
    <Section collection={orderable} name="Order By" isSub={true}>
      <Box marginTop={1}>
        <Table columns={columns} data={data} />
      </Box>
    </Section>
  )
}

const HTTPMethodBadge = ({ method }) => {
  const color = (function (method) {
    switch (method) {
      case 'GET':
        return 'registrations'
      case 'POST':
        return 'services'
      case 'PATCH':
        return 'check-ins'
      case 'DELETE':
        return 'groups'
      default:
        throw new Error(`Unknown HTTP method: ${method}`)
    }
  })(method)

  return <Badge size="sm" title={method} fontWeight={600} color={color} />
}

const Endpoint = ({ path }) => {
  return (
    <Box position="relative" paddingRight={3}>
      <Code>{trimUrl(path)}</Code>
      <CopyPath path={pathWithEnv(path)} />
    </Box>
  )
}

const Endpoints = ({ permissions, path, collectionOnly, inboundEdges }) => {
  const {
    can_create,
    can_update,
    can_destroy,
    read,
    create,
    update,
    destroy,
    create_assignable,
    update_assignable,
    edges,
  } = permissions.attributes

  const canReadResource = !collectionOnly

  const resourcePath = `${path}/{id}`

  const createEdge =
    can_create && edges[0] && inboundEdges.data.find(e => e.id === edges[0])

  const columns = [
    {
      header: 'HTTP Method',
      cell: row => <HTTPMethodBadge method={row.method} />,
    },
    {
      header: 'Endpoint',
      cell: row => <Endpoint path={row.path} />,
    },
  ]

  const assignableColumn = {
    header: 'Assignable Attributes',
    cell: row => (
      <ul>
        {row.assignable.map(attr => (
          <li key={attr}>{attr}</li>
        ))}
      </ul>
    ),
  }

  return (
    <Section id="endpoints" name="Endpoints">
      <Section collection={permissions} name="Listing" isSub>
        <Table columns={columns} data={[{ method: 'GET', path }]} />
        {read && <Notes source={read} />}
      </Section>

      {canReadResource && <Section collection={permissions} name="Reading" isSub>
        <Table columns={columns} data={[{ method: 'GET', path: resourcePath }]} />
        {read && <Notes source={read} />}
      </Section>}

      {can_create && (
        <Section collection={permissions} name="Creating" isSub>
          <Table
            columns={columns.concat(assignableColumn)}
            data={[
              {
                method: 'POST',
                path: createEdge ? createEdge.attributes.path : path,
                assignable: create_assignable,
              },
            ]}
          />
          {create && <Notes source={create} />}
        </Section>
      )}

      {can_update && (
        <Section collection={permissions} name="Updating" isSub>
          <Table
            columns={columns.concat(assignableColumn)}
            data={[
              {
                method: 'PATCH',
                path: resourcePath,
                assignable: update_assignable,
              },
            ]}
          />
          {update && <Notes source={update} />}
        </Section>
      )}

      {can_destroy && (
        <Section collection={permissions} name="Deleting" isSub>
          <Table
            columns={columns}
            data={[
              {
                method: 'DELETE',
                path: resourcePath,
              },
            ]}
          />
          {destroy && <Notes source={destroy} />}
        </Section>
      )}
    </Section>
  )
}

const Attributes = ({ attributes }) => {
  const originalColumns = [
    {
      header: 'Name',
      cell: row => <Code>{row.name}</Code>,
    },
    {
      header: 'Type',
      cell: row => <Code>{row.type}</Code>,
    },
    {
      header: 'Description',
      cell: row => (
        <Box whiteSpace="normal" shrink={1}>
          <Markdown>{row.description || ''}</Markdown>
        </Box>
      ),
      width: 600,
    },
  ]

  const notesColumn = [
    {
      header: 'Note',
      cell: row => (
        <Box whiteSpace="normal" shrink={1}>
          <Markdown>{row.note || ''}</Markdown>
        </Box>
      ),
      css: { maxWidth: 600 },
    },
  ]
  const notes = attributes.filter(attr => attr.attributes.note)
  const columns =
    notes.length > 0 ? [...originalColumns, ...notesColumn] : originalColumns

  const data = attributes.map(attr => ({
    name: attr.attributes.name,
    type: attr.attributes.type_annotation.name,
    note: attr.attributes.note,
    description: attr.attributes.description,
  }))

  return (
    <Section id="attributes" name="Attributes" collection={attributes}>
      <Box marginTop={1}>
        <Table columns={columns} data={data} />
      </Box>
    </Section>
  )
}

const Actions = ({ actions }) => {
  const columns = [
    {
      header: 'HTTP Method',
      cell: row => <HTTPMethodBadge method={row.method} />,
    },
    {
      header: 'Endpoint',
      cell: row => <Endpoint path={row.path} />,
    },
  ]

  const descriptionColumn = {
    header: 'Description',
    cell: row => (
      <Box whiteSpace="normal" shrink={1}>
        <Markdown>{row.description || ''}</Markdown>
      </Box>
    ),
    css: { maxWidth: 600 },
  }

  return (
    <Section id="actions" name="Actions" collection={actions}>
      {actions.map((action, idx) => {
        const {
          name,
          path,
          description,
          return_type,
          details,
          can_run,
          example_body,
          deprecated,
        } = action.attributes
        const { rate_limits } = action.relationships
        return (
          <Box key={idx}>
            <Heading>
              {name}
              {deprecated && <DeprecatedBadge marginLeft={1} />}
            </Heading>
            <Table
              columns={columns.concat(description ? descriptionColumn : null)}
              data={[
                {
                  method: 'POST',
                  path,
                  description,
                },
              ]}
            />
            <StackView axis="vertical" spacing={2}>
              {details && (
                <Box>
                  <Text weight={600}>Details:</Text>
                  <Markdown>{details}</Markdown>
                </Box>
              )}
              {return_type && (
                <Box>
                  <Text weight={600}>Returns:</Text>
                  <Text>{return_type}</Text>
                </Box>
              )}
              {example_body && (
                <Box>
                  <Text weight={600}>Example Post Body:</Text>
                  <CodeBlock language="json">
                    {JSON.stringify(JSON.parse(example_body), null, 2)}
                  </CodeBlock>
                </Box>
              )}
              <Box>
                <Text weight={600}>Permissions:</Text>
                <Text>{can_run || 'Must be authenticated'}</Text>
              </Box>
              {objectHasData((rate_limits || {}).data) && (
                <Box>
                  <Text weight={600} marginBottom={1}>
                    Rate Limits:
                  </Text>
                  <RateLimitsTable
                    rateLimits={(rate_limits || {}).data || []}
                  />
                </Box>
              )}
            </StackView>
          </Box>
        )
      })}
    </Section>
  )
}

const EdgeDetails = ({ details }) => {
  return details ? <Markdown>{details}</Markdown> : null
}

const EdgeScopes = ({ scopes }) => {
  return scopes.length === 0 ? null : (
    <List>
      {scopes.map(value => {
        return (
          <List.Item key={value.name}>
            <Markdown>
              {`\`${value.name}\`${
                value.scope_help ? ` — ${value.scope_help}` : ''
              }`}
            </Markdown>
          </List.Item>
        )
      })}
    </List>
  )
}

const Inbound = ({ edges, appName, version }) => {
  return (
    <Section id="belongs-to" name="Belongs To" collection={edges}>
      {edges.map(edge => {
        const { tail } = edge.relationships
        const { deprecated, details, path, scopes } = edge.attributes
        const { id, attributes } = tail.data
        const columns = [
          {
            header: 'HTTP Method',
            cell: row => <HTTPMethodBadge method={row.method} />,
          },
          {
            header: 'Endpoint',
            cell: row => <Endpoint path={row.path} />,
          },
          {
            header: 'Association',
            cell: row => (
              <RouterLink
                to={`/apps/${row.appName}/${row.version}/vertices/${row.id}`}
              >
                {row.name}
              </RouterLink>
            ),
          },
          {
            header: 'Details',
            cell: row => <EdgeDetails details={row.details} />,
          },
          {
            header: 'Filter By',
            cell: row => <EdgeScopes scopes={row.scopes} />,
          },
        ]

        return (
          <Box key={edge.id}>
            <Heading>
              {attributes.name}
              {deprecated && <DeprecatedBadge marginLeft={1} />}
            </Heading>
            <Table
              columns={columns}
              data={[
                {
                  method: 'GET',
                  path,
                  appName,
                  version,
                  id,
                  name: attributes.name,
                  scopes,
                  details,
                },
              ]}
            />
          </Box>
        )
      })}
    </Section>
  )
}

const Related = ({ edges, appName, version }) => {
  return (
    <Section id="associations" name="Associations" collection={edges}>
      {edges.map(edge => {
        const { head } = edge.relationships
        const { name, path, deprecated, details, scopes } = edge.attributes
        const { id, attributes } = head.data
        const columns = [
          {
            header: 'HTTP Method',
            cell: row => <HTTPMethodBadge method={row.method} />,
          },
          {
            header: 'Endpoint',
            cell: row => <Endpoint path={row.path} />,
          },
          {
            header: 'Returns',
            cell: row => (
              <RouterLink
                to={`/apps/${row.appName}/${row.version}/vertices/${row.id}`}
              >
                {row.name}
              </RouterLink>
            ),
          },
          {
            header: 'Details',
            cell: row => <EdgeDetails details={row.details} />,
          },
          {
            header: 'Filter By',
            cell: row => <EdgeScopes scopes={row.scopes} />,
          },
        ]

        const data = [
          {
            method: 'GET',
            path,
            appName,
            version,
            id,
            name: attributes.name,
            details,
            scopes,
          },
        ]

        return (
          <Box key={edge.id}>
            <Heading>
              {name}
              {deprecated && <DeprecatedBadge marginLeft={1} />}
            </Heading>
            <Box marginTop={1}>
              <Table columns={columns} data={data} />
            </Box>
          </Box>
        )
      })}
    </Section>
  )
}

const Relationships = ({ relationships }) => {
  const columns = [
    {
      header: 'Name',
      cell: 'name',
    },
    {
      header: 'Type',
      cell: 'type',
    },
    {
      header: 'Association Type',
      cell: 'association',
    },
    {
      header: 'Note',
      cell: row => (
        <Box whiteSpace="normal" shrink={1}>
          <Markdown>{row.note}</Markdown>
        </Box>
      ),
      css: { maxWidth: 600 },
    },
  ]

  const data = relationships.map(relationship => ({
    name: relationship.attributes.name,
    type: relationship.attributes.polymorphic
      ? '(polymorphic)'
      : relationship.attributes.graph_type,
    association: relationship.attributes.association,
    note: relationship.attributes.note,
  }))

  return (
    <Section id="relationships" name="Relationships" collection={relationships}>
      <Box marginTop={1}>
        <Table columns={columns} data={data} />
      </Box>
    </Section>
  )
}

const Content = ({ vertex, appName, version }) => {
  if (!vertex) {
    return null
  }
  const { name, description, example, path, collection_only, deprecated } =
    vertex.attributes
  const {
    permissions,
    attributes,
    actions,
    outbound_edges,
    inbound_edges,
    relationships,
    can_include,
    can_order,
    can_query,
    per_page,
    offset,
    rate_limits,
  } = vertex.relationships
  return (
    <StackView axis="vertical" spacing={4}>
      <StackView axis="vertical" spacing={2}>
        <PageHeading marginBottom={description ? 1 : 3}>{name}</PageHeading>

        {deprecated && (
          <Alert
            axis="horizontal"
            alignment="center"
            stroke="error"
            backgroundColor="error-lighter"
            color="error-dark"
            fontWeight={600}
          >
            <Icon name="general.exclamationTriangleFilled" marginRight={1} />
            Deprecated
          </Alert>
        )}

        {collection_only && (
          <Alert
            stroke="primary-light"
            backgroundColor="primary-lighter"
            color="primary"
            fontWeight={600}
          >
            Collection Only
          </Alert>
        )}

        {description && <Markdown>{description}</Markdown>}

        <Section id="example-request" name="Example Request">
          <CodeBlock language="bash">{`curl ${pathWithEnv(path)}`}</CodeBlock>
          <Link
            to={explorerUrl(path)}
            external
            style={{ margin: '0.5em 0', display: 'inline-block' }}
          >
            View in API Explorer
          </Link>
        </Section>

        <Section id="example-object" name="Example Object">
          <CodeBlock language="json">
            {JSON.stringify(JSON.parse(example), null, 2)}
          </CodeBlock>
        </Section>
      </StackView>

      {objectHasData((rate_limits || {}).data) && (
        <Section id="rate-limits" name="Endpoint Dependent Rate Limits">
          <RateLimitsTable rateLimits={(rate_limits || {}).data || []} />
        </Section>
      )}

      <Attributes attributes={attributes.data} />
      <Relationships relationships={relationships.data} />
      <Section id="url-parameters" name="URL Parameters">
        <Includeable includable={can_include.data} />
        <Orderable orderable={can_order.data} />
        <Queryable queryable={can_query.data} />
        <Pagination perPage={per_page.data} offset={offset.data} />
      </Section>
      <Endpoints
        permissions={permissions.data}
        path={path}
        collectionOnly={collection_only}
        inboundEdges={inbound_edges}
      />
      <Actions actions={actions.data} />
      <Related
        edges={outbound_edges.data}
        appName={appName}
        version={version}
      />
      <Inbound edges={inbound_edges.data} appName={appName} version={version} />
    </StackView>
  )
}

export default class Vertex extends React.Component {
  state = {
    isLoading: true,
    vertex: null,
    error: null,
  }

  componentDidMount() {
    this.fetchData()
  }

  UNSAFE_componentWillReceiveProps(next) {
    if (
      next.match.params.appName !== this.props.match.params.appName ||
      next.match.params.id !== this.props.match.params.id ||
      next.match.params.version !== this.props.match.params.version
    ) {
      this.setState({ vertex: null, actionsOpen: false }, this.fetchData)
    }
  }

  render() {
    const { appName, version } = this.props.match.params
    const { isLoading, vertex, error } = this.state
    return (
      <ErrorUI error={error} onRetry={this.fetchData}>
        <Loading isLoading={isLoading} size="xxl" style={{ paddingTop: 24 }}>
          <Content vertex={vertex} appName={appName} version={version} />
        </Loading>
      </ErrorUI>
    )
  }

  fetchData = () => {
    this.setState({ isLoading: true, error: null }, () => {
      const { appName, id, version } = this.props.match.params
      request(`/${appName}/v2/documentation/${version}/vertices/${id}`)
        .then(response => {
          this.setState({ vertex: response.data.data })
        })
        .catch(error => {
          this.setState({ error })
        })
        .finally(() => {
          this.setState({ isLoading: false })
        })
    })
  }
}
